import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, IconButton, TextField, Typography, Tooltip } from "@mui/material";
import ChatBubble from "./ChatBubble";
import { v4 as uuidv4 } from "uuid";
import { format } from "date-fns";
import ChatInterfaceStyles from "./ChatInterfaceStyles";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ThemeCreator from "@Themes/themeCreator";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import { useSelector } from "react-redux";
import { ORDER_COMMENTS } from "@API/APIRoutes";
import useGetAll from "@Hooks/useGetAll";
import usePostItem from "@Hooks/usePostItem";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import ReplyIcon from "@mui/icons-material/Reply";

const ChatInterface = ({ popupWidth, orderId, isGroupChat }) => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [visibleMessagesCount, setVisibleMessagesCount] = useState(10);
  const user = useSelector((state) => state.user);
  const userId = user?.userId;
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const theme = ThemeCreator(); // You have theme usage later maybe
  const { getAllLoading, getAll } = useGetAll();
  const { loading, postItem, error } = usePostItem();
  const [fetchingChat, setFetchingChat] = useState(false);
  const [repliedMessage, setRepliedMessage] = useState(null);
  // Fill dummy messages initially
  useEffect(() => {
    setFetchingChat(true);
    setMessages([]);
    setVisibleMessagesCount(10);
    getAll(ORDER_COMMENTS + "/" + orderId, `Failed to fetch Chat`, {
      isGroupChat: isGroupChat,
    })
      .then((fetchedData) => {
        setFetchingChat(false);
        setMessages((prevMessages) => [...prevMessages, ...fetchedData]);
      })
      .catch((error) => console.error(error));
  }, [isGroupChat]);

  const [tempId, setTempId] = useState();
  const handleSendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed) return;

    // ① Make a unique tempId for the "sending..." placeholder
    setTempId(uuidv4());

    // ② Build the placeholder message
    const sendingMsg = {
      id: tempId,
      userId: userId,
      username: user.username,
      comment: "loading message",
      role: user.roles[0],
      createdAt: new Date().toISOString(),
      loading: true,
      error: false,
      replyTo: repliedMessage?.commentId,
    };

    // ③ Append it
    setMessages((prev) => [...prev, sendingMsg]);

    // ④ Clear input
    setInput("");

    // ⑤ Post to server, passing tempId and trimmed comment into the callback

    const commentDto = {
      comment: trimmed,
      orderId:orderId,
      isGroupChat:isGroupChat,
      replyTo: repliedMessage?.commentId
    };

    const response = await postItem(
      ORDER_COMMENTS,
      commentDto,
      () => postMessageCallBack(trimmed, tempId),
      "",
      "",
      false
    );

    if (!response) {
      setMessages((prev) => {
        // ① take everything except the last message
        const allButLast = prev.slice(0, -1);

        // ② build the real message
        const realMsg = {
          id: uuidv4(),
          userId,
          username: user.username,
          comment: "error message",
          role: user.roles[0],
          createdAt: new Date().toISOString(),
          loading: false,
          error: true,
          replyTo: repliedMessage?.commentId,
        };

        // ③ return the new array: old messages minus the “Sending…” placeholder, plus the real one
        return [...allButLast, realMsg];
      });
    }
  };

  const postMessageCallBack = (realComment) => {
    setMessages((prev) => {
      // ① take everything except the last message
      const allButLast = prev.slice(0, -1);

      // ② build the real message
      const realMsg = {
        id: uuidv4(),
        userId,
        username: user.username,
        comment: realComment,
        role: user.roles[0],
        createdAt: new Date().toISOString(),
        loading: false,
        error: false,
        replyTo: repliedMessage?.commentId,
      };
      setRepliedMessage(null);
      // ③ return the new array: old messages minus the “Sending…” placeholder, plus the real one
      return [...allButLast, realMsg];
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !loading && input.trim() !== "") {
      handleSendMessage();
    }
  };

  const handleScroll = useCallback(() => {
    if (chatContainerRef.current) {
      const scrollTop = chatContainerRef.current.scrollTop;
      const nearTopThreshold = 450;
      if (scrollTop < nearTopThreshold) {
        setVisibleMessagesCount((prevCount) =>
          Math.min(prevCount + 10, messages.length)
        );
      }
    }
  }, [messages.length]);

  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    }, 0);

    return () => clearTimeout(timer);
  }, [messages, loading]);

  const handleReply = async (message) => {
    setRepliedMessage(message);
  };

  return (
    <ChatInterfaceStyles
      className="chatContainer"
      isDisabled={loading || fetchingChat || input.trim() === ""}
    >
      <Box
        className="messagesContainer"
        ref={chatContainerRef}
        onScroll={handleScroll}
      >
        {!fetchingChat ? (
          messages.slice(-(visibleMessagesCount + 2)).map((message) => (
            <React.Fragment key={message.id}>
              {message.userId !== userId ? (
                <Box className="chatBotMessageHeader">
                  <AccountCircleRoundedIcon />
                  <Box>
                    <Box className="nameAndRole">
                      <Typography className="chatBotMessageHeaderText">
                        {message.username}
                      </Typography>
                      <Typography className="roleText">
                        ~ {message.role}
                      </Typography>
                    </Box>
                    <Box className="leftAlignedDate">
                      <Typography className="dateHeaderText">
                        {message.createdAt
                          ? `${format(new Date(message.createdAt), "EEEE, dd MMMM yyyy hh:mm a")}`
                          : ""}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box className="userMessageHeader">
                  <Box>
                    <Box className="rightAlignedDate">
                      <Typography className="dateHeaderText">
                        {message.createdAt
                          ? `${format(new Date(message.createdAt), "EEEE, dd MMMM yyyy hh:mm a")}`
                          : ""}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              <ChatBubble
                text={message.comment}
                isBot={message.userId !== userId}
                loading={message.loading}
                error={message.error}
                popupWidth={popupWidth}
                repliedMessageText = {messages.find(msg => msg.commentId != null && msg.commentId === message.replyTo)?.comment}
              />
              {message.userId != userId && (
                <Box className="replyButtonContainer">
                  <Tooltip title="Reply" placement="top" arrow>
                    <IconButton
                      onClick={() => handleReply(message)}
                      className="replyButton"
                      size="small"
                    >
                      <ReplyIcon fontSize="small" />
                      <Typography variant="caption" sx={{ ml: 0.5 }}>
                        Reply
                      </Typography>
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </React.Fragment>
          ))
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
            width="100%"
          >
            <LoadingComponent size="48px" color="grey" />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {repliedMessage && (
        <Box
          className="replyPreview"
          sx={{
            backgroundColor: theme.palette.grey[100],
            padding: "8px",
            borderLeft: "4px solid",
            borderColor: "primary.main",
            marginBottom: "8px",
            borderRadius: "4px",
          }}
        >
          <Box display="flex" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Replying to {repliedMessage?.username}
            </Typography>
            <Typography
              variant="caption"
              sx={{ cursor: "pointer", color: "error.main" }}
              onClick={() => setRepliedMessage(null)}
            >
              Cancel
            </Typography>
          </Box>
          <Typography variant="body2" fontStyle="italic">
            {repliedMessage?.comment}
          </Typography>
        </Box>
      )}
      <Box className="inputContainer">
        <TextField
          variant="standard"
          placeholder="Add Comment..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          fullWidth
          className="textField"
          autoComplete="off"
        />
        <IconButton
          color="primary"
          onClick={handleSendMessage}
          className="sendButton"
          disabled={loading || fetchingChat || input.trim() === ""}
        >
          <SendOutlinedIcon className="sendIcon" />
        </IconButton>
      </Box>
    </ChatInterfaceStyles>
  );
};

export default ChatInterface;
